import {
    AfterViewInit,
    Directive,
    ElementRef,
    HostListener,
    OnDestroy,
    Optional,
    Renderer2,
} from '@angular/core';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { delay, startWith, take, tap } from "rxjs/operators";
import { merge, NEVER, timer } from 'rxjs';

import { KalTableComponent } from './kal-table.component';
import { SubscriptionManager } from 'src/app/_core/tools/subscription-manager';

// from
// https://stackoverflow.com/questions/54841520/how-to-combine-cdkvirtualscroll-with-cdktable
// todo a optimiser et intégrer au shared table si possible

@Directive({
    // eslint-disable-next-line @angular-eslint/directive-selector
    selector: 'cdk-virtual-scroll-viewport[kalFixedTable]',
    exportAs: 'kalFixedTable',
    standalone: false
})
export class KalFixedTableDirective<T> extends SubscriptionManager implements AfterViewInit, OnDestroy {

    private tfoot: HTMLElement;
    private thead: HTMLElement;
    private tfootTable: HTMLTableElement;
    private theadTable: HTMLTableElement;

    constructor(private el: ElementRef<HTMLElement>,
                private viewport: CdkVirtualScrollViewport,
                private renderer: Renderer2,
                @Optional() private table: KalTableComponent<T>) {
        super();
    }

    @HostListener('window:resize')
    recalculWidths(): void {

        ///// calcul cells width
        //
        //  walk through all <tr> with their <td> and store the max value in an array
        //
        const td = this.el.nativeElement.querySelectorAll('tbody td');
        const cellsWidthList = new Array(td.length).fill(0);
        const getKalColumnClassName = (el: HTMLElement) => el.classList.value.toString().split(' ').find(c => c.startsWith('kal-column-')) || null;

        td.forEach((item: HTMLTableCellElement, index) => {
            const w = item.getBoundingClientRect().width;
            const columnClassName = getKalColumnClassName(item);

            if (!!columnClassName && this.isColumnResizedManually(columnClassName)) {
                cellsWidthList[index] = this.table.resizedColumnList.find(e => e.code === columnClassName).currentWidth;
                item.style.setProperty('width', cellsWidthList[index] + 'px', 'important');
            } else {
                cellsWidthList[index] = Math.max(w, cellsWidthList[index]);
            }
        });

        //  get "thead th" and "tfoot td" elements and apply the max-width values
        const theadCells = this.thead ? this.thead.querySelectorAll('th') : [];
        const tfootCells = this.tfoot ? this.tfoot.querySelectorAll('td') : [];
        cellsWidthList.forEach((item, index) => {
            [theadCells, tfootCells]
                .filter(cells => !!cells[index])
                .forEach(cells => {
                    const columnClassName = getKalColumnClassName(cells[index]);
                    if (!!columnClassName && this.isColumnResizedManually(columnClassName)) {
                        cellsWidthList[index] = this.table.resizedColumnList.find(e => e.code === columnClassName).currentWidth;
                        ['--kal-th-width', 'width']
                            .forEach(property =>
                                cells[index].style.setProperty(property, cellsWidthList[index] + 'px', 'important')
                            );
                    } else {
                        const width = cellsWidthList[index] ? cellsWidthList[index] + 'px' : 'auto';
                        cells[index].style.setProperty('--kal-th-width', width, 'important');
                    }
                });
        });

        // calcul theadtable and tfootable width
        if (this.theadTable && this.tfootTable) {
            const contentWidth = this.getContentWidth() + 'px';
            [this.theadTable, this.tfootTable].forEach(table => table.style.width = contentWidth);
        }
    }

    private getContentWidth(): number {
        return this.viewport._contentWrapper.nativeElement.getBoundingClientRect().width;
    }

    private isColumnResizedManually(columnCodeClass: string): boolean {
        return !!this.getHeaderFromCellColumnClass(columnCodeClass)?.classList?.contains('resized');
    }

    private getHeaderFromCellColumnClass(columnCodeClass: string): HTMLTableCellElement | null {
        return this.thead?.querySelector('th.' + columnCodeClass) || null;
    }

    private createFakeTable(type: 'thead' | 'tfoot'): void {
        const cdkViewport = this.el.nativeElement;

        const table = document.createElement('table');
        table.classList.add('gup-C-table__basic');
        table.style.marginBottom = '0';
        table.append(this[type]);

        const wrapper = document.createElement('div');
        wrapper.classList.add('wrapper');
        wrapper.append(table);

        this[type + 'Table'] = table;
        cdkViewport[type === 'thead' ? 'before' : 'after'](wrapper);

    }

    private attachTheadAndTfootToViewportParent() {

        // get the viewport element
        const cdkViewport = this.el.nativeElement;

        // get thead/tfoot and attach before cdkviewport display
        this.thead = cdkViewport.querySelector('thead');
        this.tfoot = cdkViewport.querySelector('tfoot');

        this.createFakeTable('thead');
        if (this.tfoot) {
            this.createFakeTable('tfoot');
        }

        // get tfoot and thead heights
        const theadHeight = this.thead ? this.thead.getBoundingClientRect().height : 0;
        const tfootHeight = this.tfoot ? this.tfoot.getBoundingClientRect().height : 0;

        // resize viewport by removing thead and tfoot height
        this.el.nativeElement.style.height = (
                this.el.nativeElement.getBoundingClientRect().height -
                (theadHeight + tfootHeight)
            )
            + 'px';

        // calcul des largeurs de colonnes lorsque :
        merge(
            this.table?.tableRender.asObservable().pipe(delay(0)) || NEVER, // la table est rendue, besoin d'un délai car il y a beaucoup de calcul
            this.viewport.elementScrolled(), // la viewport est scroll
        )
            .pipe(
                tap(() => {
                    [
                        this.theadTable,
                        this.tfootTable
                    ].forEach(table => {
                        if (table) {
                            // find wrapper above and set scrollLeft according to viewport scroll
                            table.parentElement.scrollLeft = this.viewport.measureScrollOffset('left');
                        }
                    });
                }),
                this.untilDestroyed,
                startWith(1), // calcul width at start
                tap(() => this.recalculWidths())
            ).subscribe();
    }

    ngOnDestroy(): void {
        // destroy foot and head table
        [this.tfootTable, this.theadTable].filter(t => !!t).forEach(table => this.renderer.removeChild(table.parentElement, table));
        super.ngOnDestroy();
    }

    ngAfterViewInit(): void {

        // le plus rapide entre un scroll ou 200ms de délai
        merge(
            this.viewport.elementScrolled(),
            timer(200, 1)
        )
            .pipe(
                this.untilDestroyed,
                take(1),
                tap(() => this.attachTheadAndTfootToViewportParent())
            )
            .subscribe();
    }
}
